import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { BLUEPRINT_CARD_CHECKS, ROLE_BUILDER_CONFIG, getBlueprintVettingRows } from "constants/index";
import Card from "components/Card";
import { BUTTON_SIZE, PrimaryButton, TertiaryButton } from "components/Buttons";
import { Spinner } from "components/Loader";
import { Col, Row } from "components/Containers";
import { H5 } from "components/Text";
import Icon, { ICON_SIZE, ICON_TYPE, MessageWithIcon } from "components/Icon";
import Tags, { Count, Tag, TAG_SIZE } from "components/Tags";
import { TAG_COLORS } from "components/Tags/Tag";
import { colors, margins } from "style";

/**
 * BlueprintCard
 *
 * One blueprint the customer can pick for a role, summarizing the vetting it carries.
 *
 * @param {Object}    template    The blueprint, from the mission role templates query
 * @param {Boolean}   isSelected  Whether this blueprint is applied to the draft
 * @param {Boolean}   applying    A blueprint is being copied onto the draft
 * @param {Function}  onSelect    Called with the blueprint's uuid when its action is used
 * @param {Boolean}   showChecks  Whether to list the ID verification and background check
 */
const BlueprintCard = ({ template, isSelected, applying, onSelect, showChecks = false, ...props }) => (
  <Card css={styles.card(isSelected)} {...props}>
    <Col gap={margins.xxxsmall}>
      <Row css={styles.header} noWrap>
        <Tag {...TAG_COLORS.gray} size={TAG_SIZE.small}>
          <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Label" />
        </Tag>
      </Row>
      <H5 css={styles.title}>{template.name}</H5>
    </Col>
    <hr css={styles.divider} />
    <Row gap="0.25rem" css={styles.tags}>
      <Tags
        data={template.disciplines}
        limit={ROLE_BUILDER_CONFIG.BLUEPRINT_TAGS_LIMIT}
        unwrap
        size={TAG_SIZE.small}
        {...TAG_COLORS.purple}
      />
      <Tags
        data={template.primarySkills}
        limit={ROLE_BUILDER_CONFIG.BLUEPRINT_TAGS_LIMIT}
        unwrap
        size={TAG_SIZE.small}
        {...TAG_COLORS.pink}
      />
    </Row>
    <hr css={styles.divider} />
    <Col gap={margins.xxxsmall}>
      {getBlueprintVettingRows(template).map(({ id, icon, count }) => (
        <Row key={id} css={styles.vetting_row} noWrap>
          <MessageWithIcon
            messageId={id}
            messageValues={{ count: count || 0 }}
            iconType={icon}
            iconSize={ICON_SIZE.medium}
          />
          <CountBubble>{count || 0}</CountBubble>
        </Row>
      ))}
    </Col>
    {showChecks && (
      <>
        <hr css={styles.divider} />
        <Col gap={margins.xxxsmall}>
          {BLUEPRINT_CARD_CHECKS.map((id) => (
            <MessageWithIcon key={id} messageId={id} iconType={ICON_TYPE.plus} iconSize={ICON_SIZE.medium} isSuccess />
          ))}
        </Col>
      </>
    )}
    <div css={styles.action_container}>
      {isSelected ? (
        <TertiaryButton css={styles.action} size={BUTTON_SIZE.medium} disabled>
          {applying ? (
            <Spinner size={ICON_SIZE.large} margin="0" />
          ) : (
            <>
              <Icon type={ICON_TYPE.checkFilled} size={ICON_SIZE.medium} color="inherit" />
              <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.Selected" />
            </>
          )}
        </TertiaryButton>
      ) : (
        <PrimaryButton
          css={styles.action}
          className="blueprint-action"
          size={BUTTON_SIZE.medium}
          disabled={applying}
          onClick={() => onSelect(template.uuid)}
        >
          <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.Select" />
        </PrimaryButton>
      )}
    </div>
  </Card>
);

const CountBubble = styled(Count)`
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
`;

const styles = {
  card: (isSelected) => css`
    height: 100%;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    border-color: ${isSelected ? colors.purpleRainBase : colors.grayAnatomyLight4};
    box-shadow: ${isSelected ? `0 0 0 5px ${colors.purpleRainLight3}` : `none`};

    .blueprint-action {
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover,
    &:focus-within {
      border-color: ${colors.purpleRainBase};
      box-shadow: 0 0 0 5px ${colors.purpleRainLight3};

      .blueprint-action {
        opacity: 1;
      }
    }
  `,
  action_container: css`
    margin-top: auto;
    padding-top: ${margins.xsmall};
  `,
  action: css`
    width: 100%;
  `,
  header: css`
    align-items: flex-start;
    justify-content: space-between;
    gap: ${margins.xxsmall};
    min-height: ${ICON_SIZE.large};
  `,
  // Holds two rows of tags, so a long skill wrapping does not change the card's height.
  tags: css`
    align-items: flex-start;
    align-content: flex-start;
    min-height: 4.4rem;
  `,
  title: css`
    margin: 0;
  `,
  vetting_row: css`
    justify-content: space-between;
  `,
  divider: css`
    width: 100%;
    margin: ${margins.xxsmall} 0;
    border: 0;
    border-top: 1px solid ${colors.grayAnatomyLight4};
  `,
};

BlueprintCard.propTypes = {
  template: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string,
    disciplines: PropTypes.array,
    primarySkills: PropTypes.array,
    requirementsCount: PropTypes.number,
    questionsCount: PropTypes.number,
    interviewQuestionsCount: PropTypes.number,
    challengesCount: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool,
  applying: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  showChecks: PropTypes.bool,
};

export default BlueprintCard;
